import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  addCombineBundle,
  deleteCombineBundle,
  getAllCombineBundles,
  updateCombineBundle,
} from "../../../services/AdminService";
import { SearchEventsByParams } from "../../../services/EventsService";
import { toast } from "react-toastify";
import DropdownWithSearch from "../../../components/common/DropdownWithSearch";

const emptyBundleForm = {
  bundleId: null,
  name: "",
  discountType: "",
  amount: "",
  expirationDate: "",
  combines: [],
};

const getLocalToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const normalizeEvent = (rawEvent) => ({
  id: rawEvent.id,
  name: rawEvent.name,
  date: rawEvent.date,
  city: rawEvent.city,
  state: rawEvent.state,
});

// The CombineBundle response embeds combines via a different DTO than
// /Event/search: the id is "eventId" (not "id"), and "date" is a
// serialized DateOnly object ({ year, month, day, ... }), not a string.
const formatDateOnly = (rawDate) => {
  if (!rawDate || typeof rawDate !== "object") return rawDate ?? "";
  const month = String(rawDate.month).padStart(2, "0");
  const day = String(rawDate.day).padStart(2, "0");
  return `${rawDate.year}-${month}-${day}`;
};

const normalizeBundleCombine = (rawCombine) => ({
  id: rawCombine.eventId,
  name: rawCombine.name,
  date: formatDateOnly(rawCombine.date),
  city: rawCombine.city,
  state: rawCombine.state,
});

const normalizeBundle = (rawBundle) => {
  const rawCombines = rawBundle.combines;
  const combines = (Array.isArray(rawCombines) ? rawCombines : []).map(
    normalizeBundleCombine,
  );

  return {
    id: rawBundle.combineBundleId,
    name: rawBundle.name,
    discountType: rawBundle.discountType,
    amount: rawBundle.amount,
    expirationDate: rawBundle.expirationDate,
    combines,
    combineCount: combines.length,
  };
};

const NEVER_EXPIRES_PREFIX = "0001-01-01";

const isNeverExpires = (expirationDate) =>
  !expirationDate || expirationDate.startsWith(NEVER_EXPIRES_PREFIX);

const formatDiscount = (bundle) => {
  const amount = Number(bundle.amount) || 0;
  return bundle.discountType === "Percent Off"
    ? `${amount}%`
    : `$${amount.toFixed(2)}`;
};

const CombineBundleTable = forwardRef(function CombineBundleTable(_props, ref) {
  const apiInstance = useAxiosInstance();
  const [rawBundles, setRawBundles] = useState([]);
  const [combines, setCombines] = useState([]);
  const [form, setForm] = useState(emptyBundleForm);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [loading, setLoading] = useState(true);
  const [combinesLoading, setCombinesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [combinesError, setCombinesError] = useState(null);

  const bundles = useMemo(() => rawBundles.map(normalizeBundle), [rawBundles]);
  const totalPages = Math.ceil(bundles.length / itemsPerPage);
  const paginatedBundles = bundles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const fetchAllBundles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllCombineBundles(apiInstance);
      if (Array.isArray(data)) {
        setRawBundles(data);
      }
    } catch (error) {
      setError(error.message || "Error getting combine bundles.");
    } finally {
      setLoading(false);
    }
  }, [apiInstance]);

  useEffect(() => {
    fetchAllBundles();
  }, [fetchAllBundles]);

  useEffect(() => {
    const fetchCombines = async () => {
      setCombinesLoading(true);
      setCombinesError(null);
      try {
        const data = await SearchEventsByParams(apiInstance, "");
        if (Array.isArray(data)) {
          setCombines(data.map(normalizeEvent));
        }
      } catch (error) {
        setCombinesError(error.message || "Error getting combines.");
      } finally {
        setCombinesLoading(false);
      }
    };

    fetchCombines();
  }, [apiInstance]);

  const resetForm = useCallback(() => {
    setForm(emptyBundleForm);
  }, []);

  const openCreateForm = useCallback(() => {
    resetForm();
    setEditing(false);
    setShowForm(true);
  }, [resetForm]);

  useImperativeHandle(ref, () => ({ openCreateForm }), [openCreateForm]);

  const selectedCombineIds = useMemo(
    () => new Set(form.combines.map((c) => c.id)),
    [form.combines],
  );

  const availableCombines = useMemo(() => {
    const today = getLocalToday();
    return combines.filter(
      (c) => !selectedCombineIds.has(c.id) && c.date >= today,
    );
  }, [combines, selectedCombineIds]);

  const handleAddCombine = (combineId) => {
    if (combineId === undefined || combineId === null) return;
    const combine = combines.find((c) => String(c.id) === String(combineId));
    if (combine) {
      setForm((prev) => ({ ...prev, combines: [...prev.combines, combine] }));
    }
  };

  const handleRemoveCombine = (combineId) => {
    setForm((prev) => ({
      ...prev,
      combines: prev.combines.filter((c) => c.id !== combineId),
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Bundle name is required.");
      return;
    }

    if (form.combines.length < 2) {
      toast.error("Select at least 2 combines for this bundle.");
      return;
    }

    if (!form.discountType) {
      toast.error("Please select a discount type.");
      return;
    }

    const today = getLocalToday();
    if (form.expirationDate && form.expirationDate < today) {
      toast.error("Expiration date cannot be in the past.");
      return;
    }

    const parsedAmount = parseFloat(form.amount);
    if (form.amount === "" || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (form.discountType === "Percent Off" && parsedAmount > 100) {
      toast.error("Percentage cannot exceed 100%.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      discountType: form.discountType,
      amount: parsedAmount,
      expirationDate:
        form.expirationDate === "" ? "0001-01-01" : form.expirationDate,
      eventIds: form.combines.map((c) => c.id),
    };

    try {
      if (editing) {
        await updateCombineBundle(apiInstance, form.bundleId, payload);
        toast.success("Combine bundle updated successfully!");
      } else {
        await addCombineBundle(apiInstance, payload);
        toast.success("Combine bundle added successfully!");
      }
      await fetchAllBundles();
      resetForm();
      setShowForm(false);
      setEditing(false);
    } catch (error) {
      const backend = error.response?.data;
      const message =
        backend?.error ||
        backend?.message ||
        backend?.detail ||
        (typeof backend === "string" ? backend : null) ||
        (editing
          ? "Failed to update combine bundle."
          : "Failed to add combine bundle.");
      toast.error(message);
    }
  };

  const handleEdit = (bundle) => {
    setForm({
      bundleId: bundle.id,
      name: bundle.name,
      discountType: bundle.discountType ?? "",
      amount: bundle.amount ?? "",
      expirationDate: isNeverExpires(bundle.expirationDate)
        ? ""
        : bundle.expirationDate.split("T")[0],
      combines: bundle.combines,
    });

    setEditing(true);
    setShowForm(true);
  };

  const handleDelete = async () => {
    const deleteId = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteCombineBundle(apiInstance, deleteId);
      setRawBundles((prev) =>
        prev.filter((b) => b.combineBundleId !== deleteId),
      );
      toast.success("Combine bundle deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete combine bundle.");
    }
  };

  const utcToLocalDate = (utcDateString) => {
    const date = new Date(utcDateString);
    return date.toLocaleDateString();
  };

  return (
    <>
      {loading ? (
        <div className="hidden">Loading...</div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2 text-left">Bundle Name</th>
                  <th className="border p-2 text-left">Combines</th>
                  <th className="border p-2 text-left">Discount Type</th>
                  <th className="border p-2 text-left">Expiration</th>
                  <th className="border p-2 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {paginatedBundles.map((bundle) => (
                  <tr key={bundle.id}>
                    <td className="border p-2 whitespace-nowrap">
                      {bundle.name}
                    </td>
                    <td className="border p-2 whitespace-nowrap">
                      {bundle.combineCount}
                    </td>
                    <td className="border p-2 whitespace-nowrap">
                      {formatDiscount(bundle)}
                    </td>
                    <td className="border p-2 whitespace-nowrap">
                      {isNeverExpires(bundle.expirationDate)
                        ? "Never"
                        : utcToLocalDate(bundle.expirationDate)}
                    </td>
                    <td className="border p-2 space-x-2 whitespace-nowrap">
                      <button
                        className="px-3 py-1  text-blue-700"
                        onClick={() => handleEdit(bundle)}
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                      </button>
                      <button
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700"
                        onClick={() => setConfirmDeleteId(bundle.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />{" "}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row justify-end items-center mt-4 space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span className="px-2 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? "Edit Combine Bundle" : "Add Combine Bundle"}
            </h3>
            <form onSubmit={handleFormSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Bundle Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full border p-2 rounded"
                  required
                />
              </div>
              <div>
                <DropdownWithSearch
                  label={
                    <>
                      Select Combines <span className="text-red-600">*</span>
                    </>
                  }
                  options={availableCombines.map((combine) => ({
                    label: `${combine.date} - ${combine.name} - ${combine.city}, ${combine.state}`,
                    value: combine.id,
                  }))}
                  placeholder={
                    combinesLoading ? "Loading combines..." : "Select combine"
                  }
                  onSelect={(selected) => handleAddCombine(selected[0]?.value)}
                  allowMultiple={false}
                  isLoading={combinesLoading}
                  isObjectArray={true}
                  containerClassName="relative w-full"
                  buttonClassName="w-full flex justify-between items-center border p-2 rounded shadow-sm bg-white"
                />
                {combinesError && (
                  <p className="text-red-600 text-xs mt-1">{combinesError}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.combines.map((combine) => (
                    <span
                      key={combine.id}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-sm"
                    >
                      {combine.date} - {combine.name} - {combine.city},{" "}
                      {combine.state}
                      <button
                        type="button"
                        onClick={() => handleRemoveCombine(combine.id)}
                        className="text-gray-600 hover:text-red-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Discount Type
                </label>
                <select
                  value={form.discountType || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      discountType: e.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                  required
                >
                  <option value="">Select discount type</option>
                  <option value="Amount Off">Amount Off</option>
                  <option value="Percent Off">Percent Off</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className="w-full border p-2 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Expiration
                </label>
                <input
                  type="date"
                  value={form.expirationDate}
                  min={getLocalToday()}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      expirationDate: e.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-500 text-white rounded"
                >
                  {editing ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p>Are you sure you want to delete this combine bundle?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default CombineBundleTable;
